import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { extractError } from "@/lib/api";
import i18n, { LS_LANG_KEY, ensureHindiLoaded } from "@/i18n";

const AuthContext = createContext(null);

/**
 * Marker localStorage sets when the user EXPLICITLY picks a language via the
 * LanguageSwitcher. Once set, we never let the server-side profile silently
 * override that choice on later bootstraps — otherwise a stale/lagging profile
 * write would flip the UI back to the old preference on every reload.
 */
const LS_LANG_USER_SET = "venturekhoj_lang_user_set";

async function syncLangFromProfile({ force = false } = {}) {
  // Pull user's saved language preference and apply it to i18next.
  // R109.33 — only sync from server when either (a) the user has NEVER
  // picked a language locally, or (b) the caller explicitly forces it
  // (e.g. right after login on a new device). This prevents the profile
  // read from clobbering the local choice on subsequent page bootstraps.
  try {
    if (!force) {
      const userSet = localStorage.getItem(LS_LANG_USER_SET);
      if (userSet === "1") return;
    }
    const { data } = await api.get("/profile");
    const lng = data?.profile?.language;
    if (lng && ["en", "hi"].includes(lng) && i18n.language !== lng) {
      // R109.34 — Hindi resources are lazy-loaded. Await the bundle BEFORE
      // flipping the language so the very first render post-change already
      // has Hindi translations — no English-fallback flash and no missed
      // re-render for components that use `useTranslation` but don't consume
      // `t()` on every render.
      if (lng === "hi") {
        try { await ensureHindiLoaded(); } catch (_) {}
      }
      await i18n.changeLanguage(lng);
      try { localStorage.setItem(LS_LANG_KEY, lng); } catch (err) { console.warn("lang persist failed", err); }
    }
  } catch (err) {
    // Non-fatal: language pref is best-effort, user stays in default until they explicitly set it
    if (process.env.NODE_ENV !== "production") console.debug("syncLangFromProfile skipped", err);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("venturekhoj_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      // R109.33 — passive sync on bootstrap: only apply server-side language
      // preference if the user has never explicitly picked one locally.
      syncLangFromProfile();
    } catch (err) {
      // Token invalid / expired — clear it and force re-login
      if (process.env.NODE_ENV !== "production") console.debug("bootstrap failed, clearing token", err);
      localStorage.removeItem("venturekhoj_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("venturekhoj_token", data.token);
    setUser(data.user);
    // R109.33 — fresh login: always adopt the server-side preference (user
    // may be on a new device where they've never explicitly picked a lang).
    syncLangFromProfile({ force: true });
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    localStorage.setItem("venturekhoj_token", data.token);
    setUser(data.user);
    syncLangFromProfile({ force: true });
    return data.user;
  };

  const loginWithToken = (token, userObj) => {
    localStorage.setItem("venturekhoj_token", token);
    setUser(userObj);
    syncLangFromProfile({ force: true });
    return userObj;
  };

  const logout = () => {
    localStorage.removeItem("venturekhoj_token");
    setUser(null);
  };

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch (_) {
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithToken, logout, refresh, bootstrap, extractError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
